// src/mocks/handlers/dashboard/topCampaigns.ts

import { rest } from 'msw';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import {
  ITopCampaignsResponse,
  ITopCampaignsItem,
  TTopCampaignsOrdering,
} from '@/domains/dashboard/topCampaigns/topCampaigns.types';
import topCampaignsData from '@/mocks/data/dashboard_campaigns.json';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';

type TopCampaignsMockEntry = {
  customer_uuid: string;
} & ITopCampaignsResponse;

export const topCampaignsHandler = rest.get<ITopCampaignsResponse>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.DASHBOARD_TOP_CAMPAIGNS}`,
  (req, res, ctx) => {
    const { customer_uuid } = req.params;

    // query params
    const url = new URL(req.url);
    const params = url.searchParams;

    const pageParam = params.get('page') ?? '1';
    const pageSizeParam = params.get('page_size') ?? '5';
    const orderingParam =
      (params.get('ordering') as TTopCampaignsOrdering | null) ??
      'advanced_amount';

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    // 1. filter data by customer_uuid
    const topCampaigns = (topCampaignsData as TopCampaignsMockEntry[]).find(
      (entry: TopCampaignsMockEntry) => entry.customer_uuid === customer_uuid,
    );

    if (!topCampaigns) {
      return res(
        ctx.status(404),
        ctx.json({ error: 'Top campaigns data not found' }),
      );
    }

    // 2. sort
    const sortedResults = [...topCampaigns.results];
    sortedResults.sort((a: ITopCampaignsItem, b: ITopCampaignsItem) => {
      const aValue = a[orderingParam];
      const bValue = b[orderingParam];

      if (aValue === undefined || bValue === undefined) return 0;

      if (orderingParam === 'start_date' || orderingParam === 'end_date') {
        const aTime = new Date(aValue as string).getTime();
        const bTime = new Date(bValue as string).getTime();
        return bTime - aTime;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return bValue - aValue;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return bValue.localeCompare(aValue);
      }

      return 0;
    });

    // 3. paginate
    const page = parseInt(pageParam, 10);
    const pageSize = parseInt(pageSizeParam, 10);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedResults = sortedResults.slice(startIndex, endIndex);

    const response: ITopCampaignsResponse = {
      active_campaigns: topCampaigns.active_campaigns,
      results: paginatedResults,
    };

    return res(ctx.status(200), ctx.json(response));
  },
);
